import { useMemo } from 'react'
import { History, User, Clock, AlertTriangle, ArrowRight } from 'lucide-react'
import { Badge, type BadgeTone } from '../ui/Badge'
import type { DocumentChangeLogDto } from '../../services/procurement/types'

export interface DocumentHistoryTabProps {
  logs: DocumentChangeLogDto[] | null
  loading?: boolean
  error?: string | null
  emptyMessage?: string
}

const ACTION_CONFIG: Record<
  string,
  { label: string; tone: BadgeTone }
> = {
  CREATE: { label: 'Tạo mới', tone: 'success' },
  UPDATE: { label: 'Cập nhật', tone: 'info' },
  HEADER_UPDATE: { label: 'Sửa thông tin chung', tone: 'info' },
  LINE_UPDATE: { label: 'Sửa dòng hàng', tone: 'warning' },
  LINE_ADD: { label: 'Thêm dòng mới', tone: 'success' },
  LINE_DELETE: { label: 'Xóa dòng', tone: 'danger' },
  CONFIRM: { label: 'Xác nhận đơn', tone: 'success' },
  CANCEL: { label: 'Hủy đơn hàng', tone: 'danger' },
  REOPEN: { label: 'Khôi phục chứng từ', tone: 'success' },
  REOPEN_FOR_EDIT: { label: 'Mở lại để sửa', tone: 'warning' },
  VOID: { label: 'Hủy chứng từ', tone: 'danger' },
}

const FIELD_LABELS: Record<string, string> = {
  customer_id: 'Khách hàng',
  buyer_id: 'Người mua',
  order_date: 'Ngày đặt hàng',
  requested_delivery_date: 'Ngày giao yêu cầu',
  requested_ship_date: 'Ngày giao dòng',
  cancel_reason: 'Lý do hủy',
  reopen_reason: 'Lý do khôi phục',
  total_quantity: 'Tổng số lượng',
  line_count: 'Số dòng',
  supplier_id: 'Nhà cung cấp',
  delivery_date: 'Ngày giao hàng',
  note: 'Ghi chú',
  material_id: 'Mã vật tư',
  order_qty: 'Số lượng đặt',
  order_uom: 'Đơn vị đặt',
  unit_price_actual: 'Đơn giá',
  price_source: 'Nguồn giá',
  price_rule_id: 'Quy tắc giá',
  manual_reason: 'Lý do giá thủ công',
  department: 'Bộ phận',
  priority: 'Độ ưu tiên',
  required_date: 'Ngày cần hàng',
  quantity: 'Số lượng',
  uom: 'Đơn vị tính',
  purpose: 'Mục đích xuất',
  return_reason: 'Lý do trả hàng',
  qty_base: 'Số lượng (đơn vị gốc)',
  status: 'Trạng thái',
  is_void: 'Đã hủy',
  void_reason: 'Lý do hủy',
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—'
  if (typeof val === 'boolean') return val ? 'Có' : 'Không'
  if (typeof val === 'number') return val.toLocaleString('vi-VN')
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val)
    } catch {
      return String(val)
    }
  }
  return String(val)
}

function computeDiff(log: DocumentChangeLogDto): Array<[string, { old: unknown; new: unknown }]> {
  if (log.diff) {
    return Object.entries(log.diff)
  }
  if (log.before || log.after) {
    const keys = Array.from(
      new Set([...Object.keys(log.before || {}), ...Object.keys(log.after || {})]),
    )
    const result: Array<[string, { old: unknown; new: unknown }]> = []
    for (const k of keys) {
      const oldVal = log.before ? log.before[k] : undefined
      const newVal = log.after ? log.after[k] : undefined
      if (oldVal !== newVal) {
        result.push([k, { old: oldVal, new: newVal }])
      }
    }
    return result
  }
  return []
}

export function DocumentHistoryTab({
  logs,
  loading = false,
  error = null,
  emptyMessage = 'Chưa có lịch sử thay đổi.',
}: DocumentHistoryTabProps) {
  const sortedLogs = useMemo(() => {
    if (!logs) return []
    return [...logs].sort((a, b) => {
      const timeA = new Date(a.at || a.created_at || 0).getTime()
      const timeB = new Date(b.at || b.created_at || 0).getTime()
      return timeB - timeA
    })
  }, [logs])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-slate-400">
        <Clock className="mr-2 h-4 w-4 animate-spin text-slate-400" />
        Đang tải lịch sử thay đổi...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-[var(--radius-md)] border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4" />
          Không thể tải lịch sử thay đổi
        </div>
        <div className="mt-1 text-xs text-red-600">{error}</div>
      </div>
    )
  }

  if (!sortedLogs || sortedLogs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[var(--radius-md)] border border-dashed border-slate-200 py-12 text-center text-slate-400">
        <History className="mb-2 h-8 w-8 text-slate-300" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <History className="h-4 w-4 text-slate-500" />
          Lịch sử thay đổi ({sortedLogs.length})
        </h3>
      </div>

      <div className="relative border-l-2 border-slate-200 pl-4 ml-2 space-y-6">
        {sortedLogs.map((log) => {
          const actionConfig = ACTION_CONFIG[log.action] || {
            label: log.action,
            tone: 'neutral',
          }
          const diffEntries = computeDiff(log)
          const timestamp = log.at || log.created_at
          const actor = log.actor_name || log.by || log.changed_by || log.actor_id || 'Hệ thống'

          return (
            <div key={log.id} className="relative group">
              {/* Timeline marker */}
              <div className="absolute -left-[23px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-slate-400 group-hover:bg-slate-600" />

              <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
                {/* Header: Action badge, After-approval badge, Actor, Timestamp */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={actionConfig.tone}>{actionConfig.label}</Badge>
                    {log.after_approval && (
                      <Badge tone="warning" className="font-semibold">
                        Đã sửa sau duyệt
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-500 tabular-nums font-mono">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {actor}
                    </span>
                    {timestamp && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(timestamp).toLocaleString('vi-VN')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Reason */}
                {log.reason && (
                  <div className="mt-2.5 rounded bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                    <span className="font-medium text-slate-500">Lý do: </span>
                    {log.reason}
                  </div>
                )}

                {/* Diff table */}
                {diffEntries.length > 0 && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-slate-100 text-slate-400">
                          <th className="py-1 font-medium w-1/3">Trường thay đổi</th>
                          <th className="py-1 font-medium w-1/3">Giá trị cũ</th>
                          <th className="py-1 font-medium w-1/3">Giá trị mới</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {diffEntries.map(([field, delta]) => {
                          const fieldLabel = FIELD_LABELS[field] || field
                          return (
                            <tr key={field} className="hover:bg-slate-50/50">
                              <td className="py-1.5 font-medium text-slate-700">
                                {fieldLabel}
                              </td>
                              <td className="py-1.5 text-red-600 line-through tabular-nums">
                                {formatValue(delta.old)}
                              </td>
                              <td className="py-1.5 text-emerald-700 font-medium tabular-nums flex items-center gap-1">
                                <ArrowRight className="h-3 w-3 text-slate-400 shrink-0 inline" />
                                {formatValue(delta.new)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
